/**
 * A FULL-VIEW LIGHTBOX FOR A CARD'S IMAGE ATTACHMENTS (dossier v3 §7, preview C).
 *
 * Opened from an image thumbnail in the Files fold. Shows the image large over a
 * dimmed backdrop, steps through the card's OTHER images with the arrows or the
 * ← → keys, and clicking the image toggles fit-to-view / actual size. It is a real
 * dialog — focus-trapped, Escape to close, click-the-backdrop to close — reusing
 * the app's own motion primitives, so it behaves like every other overlay.
 *
 * It holds no files of its own: it asks the store for a short-lived address for
 * whichever image is current (store.cardFileUrl) and refetches when you step.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CardAttachment } from '../types';
import { useFocusTrap, useEscapeToClose } from '../motion';
import Icon from './Icon';

export function CardFileLightbox({
  images,
  index,
  onIndex,
  onClose,
  urlFor,
}: {
  images: CardAttachment[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  urlFor: (attId: string) => Promise<string>;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  // Glide out before unmounting (R32): the leave runs, then the parent drops us.
  const close = () => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(onClose, 160);
  };
  useFocusTrap(panelRef, true);
  // Capture phase: the lightbox opens OVER the card's own window Escape listener,
  // which is not a useEscapeToClose dialog. Without capture, one Escape would close
  // the lightbox AND the card behind it. Capturing takes the key for the top layer.
  useEscapeToClose(close, true, true);

  const [url, setUrl] = useState('');
  const [zoomed, setZoomed] = useState(false);
  const current = images[index];

  // A fresh address for whichever image is current; cleared while it is fetched so
  // the previous image never lingers under the new one. Zoom resets on every step.
  useEffect(() => {
    let stale = false;
    setUrl('');
    setZoomed(false);
    void urlFor(current.id).then((u) => {
      if (!stale) setUrl(u);
    });
    return () => {
      stale = true;
    };
  }, [current.id, urlFor]);

  // The arrows also answer the ← → keys. Only when there is more than one image.
  useEffect(() => {
    if (images.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onIndex((index - 1 + images.length) % images.length);
      else if (e.key === 'ArrowRight') onIndex((index + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, onIndex]);

  return createPortal(
    <div
      className={`cfl-scrim${closing ? ' is-closing' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="cfl-panel"
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={current.name}
      >
        <div className="cfl-top">
          <span className="cfl-name" title={current.name}>
            {current.name}
          </span>
          {images.length > 1 && (
            <span className="cfl-count">
              {index + 1} of {images.length}
            </span>
          )}
          <button className="btn-icon icon-act cfl-close" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        <div className={`cfl-stage${zoomed ? ' is-zoomed' : ''}`}>
          {url ? (
            <img
              className="cfl-img"
              src={url}
              alt={current.name}
              onClick={() => setZoomed((z) => !z)}
              title={zoomed ? 'Fit to view' : 'Actual size'}
            />
          ) : (
            <p className="cfl-wait">Loading…</p>
          )}
        </div>

        {images.length > 1 && (
          <>
            <button
              className="cfl-nav cfl-prev"
              onClick={() => onIndex((index - 1 + images.length) % images.length)}
              aria-label="Previous image"
            >
              <Icon name="chevron" />
            </button>
            <button
              className="cfl-nav cfl-next"
              onClick={() => onIndex((index + 1) % images.length)}
              aria-label="Next image"
            >
              <Icon name="chevron" />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
